import React, { useState } from 'react';
import { Modal, Form, Button, TextArea, Message } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { authUtil } from '../../utils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  featureName: string;
  url?: string;
}

interface FormData {
  userFeedback: string;
}

interface FormErrors {
  userFeedback?: string;
}

const FeedbackModal: React.FC<Props> = ({
  isOpen,
  onClose,
  featureName,
  url,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({ userFeedback: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.userFeedback.trim()) {
      newErrors.userFeedback = t('FeedbackRequired');
    } else if (formData.userFeedback.trim().length < 5) {
      newErrors.userFeedback = t('FeedbackTooShort', { min: 5 });
    } else if (formData.userFeedback.trim().length > 5000) {
      newErrors.userFeedback = t('FeedbackTooLong', { max: 5000 });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const idToken = await authUtil.getFreshIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/feedback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'br-token': idToken,
          },
          body: JSON.stringify({
            featureName,
            userFeedback: formData.userFeedback.trim(),
            url,
          }),
        }
      );

      if (response.ok) {
        toast.success(t('FeedbackSubmittedSuccessfully'));
        onClose();
        setFormData({ userFeedback: '' });
        setErrors({});
      } else {
        const errorData = await response.json();
        if (errorData.error === 'Validation failed' && errorData.details) {
          // Handle validation errors from API
          const newErrors: FormErrors = {};
          errorData.details.forEach((detail: any) => {
            if (detail.field === 'userFeedback') {
              newErrors.userFeedback = detail.message;
            }
          });
          setErrors(newErrors);
        } else {
          toast.error(t('FeedbackSubmissionFailed'));
        }
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error(t('FeedbackSubmissionFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setFormData({ userFeedback: '' });
      setErrors({});
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="small"
      closeIcon={!isSubmitting}
    >
      <Modal.Header>
        {t('FeedbackModalTitle', { feature: featureName })}
      </Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field error={!!errors.userFeedback}>
            <label>{t('YourFeedback')}</label>
            <TextArea
              placeholder={t('FeedbackPlaceholder')}
              value={formData.userFeedback}
              onChange={(e, { value }) =>
                handleInputChange('userFeedback', value as string)
              }
              rows={4}
              maxLength={5000}
              disabled={isSubmitting}
            />
            <div style={{ fontSize: '0.8em', color: '#999', marginTop: '4px' }}>
              {formData.userFeedback.length}/5000
            </div>
          </Form.Field>
          {errors.userFeedback && (
            <Message negative size="tiny">
              {errors.userFeedback}
            </Message>
          )}
        </Form>
        <div style={{ marginTop: '16px', fontSize: '0.9em', color: '#666' }}>
          {t('FeedbackDisclaimer')}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          {t('Cancel')}
        </Button>
        <Button
          primary
          loading={isSubmitting}
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {t('Submit')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default FeedbackModal;
